import { Button, Link, Modal, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { FreeFocusInside } from 'react-focus-lock';
import styled from 'styled-components';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import { getSelectedLanguage } from '@frontend/translate';
import type { FilesUrlBase } from '../../../../common/enums/filesUrlBase';
import type { IAsset } from '../../interfaces/asset';
import { FullScreen } from '../FullScreen';
import { Viewer } from '../Viewer';
import { generateUrl } from './viewerModalUtils';

type ViewerModalProps = {
    assets: (IAsset | ProcessActivityAsset)[];
    group?: string;
    hideThumbnail?: boolean;
    id?: string;
    isPdf: boolean;
    onClose: () => void;
    selectedFileId?: string;
    url: string;
    urlBase?: FilesUrlBase;
};

export function ViewerModal({
    assets,
    group,
    hideThumbnail,
    id,
    isPdf,
    onClose,
    selectedFileId,
    url: urlProp,
    urlBase,
}: ViewerModalProps) {
    const { viewer } = getSelectedLanguage();
    const [fullScreen, setFullScreen] = useState(false);
    const [newSelectedFileId, setNewSelectedFileId] = useState(selectedFileId);
    const [selectedFileIsPdf, setSelectedFileIsPdf] = useState(isPdf);
    const [url, setUrl] = useState<string>(urlProp);
    const [linkUrl, setLinkUrl] = useState<string>();

    useEffect(() => {
        setLinkUrl(generateUrl(url, id, group, urlBase, selectedFileIsPdf, newSelectedFileId));
    }, [url, newSelectedFileId, selectedFileIsPdf, id, group, urlBase]);

    useEffect(() => {
        setUrl(urlProp);
    }, [urlProp]);

    return (
        <>
            {!fullScreen && (
                <StyledModal
                    autoFocus={false}
                    isOpen={true}
                    onClose={onClose}
                    size="fullscreen"
                >
                    <FreeFocusInside className="free-focus-inside">
                        <Modal.Header>
                            {viewer.viewer}
                            <Text
                                as="span"
                                size="sm"
                            >
                                {!selectedFileIsPdf && (
                                    <Button
                                        className="automation-fullscreen"
                                        onClick={() => {
                                            setFullScreen(true);
                                        }}
                                        sx={{ ml: 4 }}
                                        variant="outline"
                                    >
                                        {viewer.fullScreen}
                                    </Button>
                                )}
                                {linkUrl && (
                                    <Link
                                        href={linkUrl}
                                        iconRight="external"
                                        onClick={async () => onClose()}
                                        openInNewTab={true}
                                        sx={{ ml: 4 }}
                                        variant="externalLink"
                                    >
                                        {viewer.openNewTab}
                                    </Link>
                                )}
                            </Text>
                        </Modal.Header>
                        <Modal.CloseButton />
                        <Modal.Body>
                            <Viewer
                                files={assets}
                                group={group}
                                id={id}
                                selectedFileId={selectedFileId}
                                setSelectedFileId={setNewSelectedFileId}
                                setSelectedFileType={setSelectedFileIsPdf}
                                setUrl={setUrl}
                                showThumbnails={!hideThumbnail}
                                urlBase={urlBase}
                            />
                        </Modal.Body>
                        <Modal.Footer />
                    </FreeFocusInside>
                </StyledModal>
            )}
            {fullScreen && (
                <FullScreen handleClose={() => setFullScreen(false)}>
                    <Viewer
                        files={assets}
                        group={group}
                        id={id}
                        selectedFileId={selectedFileId}
                        showThumbnails={false}
                        urlBase={urlBase}
                    />
                </FullScreen>
            )}
        </>
    );
}

const StyledModal = styled(Modal)`
    .free-focus-inside {
        height: 100%;
        display: flex;
        flex-direction: column;
    }
`;
